import React from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../../components-new/ui/Modal';
import Translate from '../../../../../components/Translate';
import KeyboardIcon from '../../../../../assets/icons/KeyboardIcon';
import config from '../../../../../config';
import {generalModalStyles} from '../../../../../utils/General';
import {hotkeyKeys} from '../../../../../utils/hotkeys';
import '../chat-css/HotkeysCheatSheet.css';

Modal.setAppElement('#root');

/*
 * Which key does what, without leaving the chat.
 *
 * The combinations were only ever visible on the screen where they are set -
 * My space, three clicks and a page away from the conversation someone is in
 * the middle of. An operator who could not remember one had to abandon the chat
 * to go and look, which is why most of them never learned any.
 *
 * Nothing here can be changed: this is the sheet pinned beside the keyboard,
 * not the settings page.
 */
/*
 * Three of these are sold with the package rather than with the product, and
 * the operator whose company has not bought one has no button for it either.
 * The sheet is what the chat can do for this operator, so it leaves them out.
 */
const REQUIRES_FEATURE = {
    OPEN_FORWARD_DIALOG: config.features.CONVERSATION_FORWARD,
    OPEN_BLOCK_USER_MODAL: config.features.BLACK_LIST,
    OPEN_TEMPLATES_MODAL: config.features.TEMPLATES,
};

const GROUPS = [
    {
        title: 'hotkeysInConversation',
        actions: [
            'OPEN_INFO_MODAL',
            'OPEN_SEARCH_MODAL',
            'OPEN_FILES_MODAL',
            'OPEN_SAVE_INFO_MODAL',
            'OPEN_FORWARD_DIALOG',
            'OPEN_BLOCK_USER_MODAL',
            'OPEN_END_CHAT_MODAL',
        ],
    },
    {
        title: 'hotkeysInMessage',
        actions: [
            'OPEN_TEMPLATES_MODAL',
            'OPEN_EMOJI_MODAL',
            'SWITCH_PRIVATE_MESSAGE_MODE',
        ],
    },
    {
        title: 'hotkeysNavigation',
        actions: [
            'NEXT_CHAT',
            'PREVIOUS_CHAT',
            'FOCUS_MESSAGE_INPUT',
            'OPEN_HOTKEYS_HELP',
        ],
    },
];

const modalStyles = {
    ...generalModalStyles.standardModalStyles,
    content: {
        ...generalModalStyles.standardModalStyles.content,
        width: '620px',
        maxWidth: 'calc(100vw - 32px)',
    },
};

const HotkeysCheatSheet = ({isOpen, onRequestClose, hotkeys, features}) => {
    const allowed = (action) => {
        const feature = REQUIRES_FEATURE[action];
        return !feature || features.includes(feature);
    };

    const byAction = {};
    hotkeys.forEach((hk) => {
        if (hk.action && hk.hotkey && allowed(hk.action)) byAction[hk.action] = hk.hotkey;
    });

    const grouped = GROUPS
        .map(group => ({
            title: group.title,
            rows: group.actions
                .filter(action => byAction[action])
                .map(action => ({action, hotkey: byAction[action]})),
        }))
        .filter(group => group.rows.length > 0);

    /*
     * An action the server knows about and this file does not - a new one added
     * after this sheet was written. It is shown rather than dropped, under the
     * last heading, so the sheet is never quietly incomplete.
     */
    const placed = GROUPS.reduce((all, group) => all.concat(group.actions), []);
    const rest = hotkeys.filter(hk => hk.action && hk.hotkey && allowed(hk.action)
        && placed.indexOf(hk.action) === -1);

    if (rest.length > 0 && grouped.length > 0) {
        grouped[grouped.length - 1].rows = grouped[grouped.length - 1].rows.concat(
            rest.map(hk => ({action: hk.action, hotkey: hk.hotkey}))
        );
    }

    return (
        <Modal
            footer={null}
            isOpen={isOpen}
            closeTimeoutMS={config.closeTimeoutMS}
            onRequestClose={onRequestClose}
            style={modalStyles}
            headerIcon={<KeyboardIcon width={20} height={20} stroke="currentColor"/>}
            title={<Translate text="hotkeys"/>}
            description={<Translate text="hotkeysCheatSheetNote"/>}
            modalClassName="hotkeys-sheet"
        >
            <div className="hotkeys-sheet__body">
                {grouped.map(group => (
                    <section className="hotkeys-sheet__group" key={group.title}>
                        <h4 className="hotkeys-sheet__group-title">
                            <Translate text={group.title}/>
                        </h4>

                        <div className="hotkeys-sheet__rows">
                            {group.rows.map(row => (
                                <div className="hotkeys-sheet__row" key={row.action}>
                                    <span className="hotkeys-sheet__label">
                                        <Translate text={row.action}/>
                                    </span>
                                    <span className="hotkeys-sheet__keys">
                                        {hotkeyKeys(row.hotkey).map(key => (
                                            <kbd className="hotkeys-sheet__key" key={key}>{key}</kbd>
                                        ))}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </section>
                ))}

                {grouped.length === 0 && (
                    <div className="hotkeys-sheet__empty">
                        <Translate text="noHotkeys"/>
                    </div>
                )}
            </div>
        </Modal>
    );
};

HotkeysCheatSheet.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    hotkeys: PropTypes.array.isRequired,
    features: PropTypes.array.isRequired,
};

export default HotkeysCheatSheet;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/chat/chat-components/HotkeysCheatSheet.jsx